import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { RHProvidersComponent } from "../components/ProviderInfo/RH/RHProvidersInfoComponent";
import { SCOProvidersComponent } from "../components/ProviderInfo/SCO/ScoProvidersInfoComponent";
import { Constants } from "../constants/appConstants";
import { useAppContext } from "../Context";
import ComponentHeader from "./ComponentHeader";
import { AuthContext } from "../Context/AuthContext";
import { validateOrCreateUser } from "../services/UserService";

type DashboardNavigationState = {
  providerType?: string;
};

type ErrorWithInnerException = Error & {
  innerException?: unknown;
  cause?: unknown;
};

const normalizeProviderType = (value?: string): string => {
  const normalized = String(value || "").trim().toUpperCase();

  if (normalized === Constants.SCOKey) {
    return Constants.SCOKey;
  }

  if (
    normalized === Constants.RSKey ||
    normalized === "RESIDENTIAL" ||
    normalized === "RH"
  ) {
    return Constants.RSKey;
  }

  return "";
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return "Unknown error";
};

const logError = (message: string, error: unknown): void => {
  const typedError = error as ErrorWithInnerException;

  console.error(message, {
    message: getErrorMessage(error),
    innerException: getErrorMessage(
      typedError?.innerException ?? typedError?.cause
    ),
  });
};

function Home() {
  const location = useLocation();
  const navigate = useNavigate();

  const { isUserValidated, setIsUserValidated } =
    useContext(AuthContext);

  const {
    providerType,
    reviewYear,
    setProviderType,
  } = useAppContext();

  const [providerTypeReady, setProviderTypeReady] =
    useState(false);

  const targetProviderType = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    const fromQuery = normalizeProviderType(
      searchParams.get("providerType") || ""
    );

    if (fromQuery) {
      return fromQuery;
    }

    const state =
      (location.state as DashboardNavigationState | null) ?? null;

    const fromLocationState = normalizeProviderType(
      state?.providerType
    );

    if (fromLocationState) {
      return fromLocationState;
    }

    return normalizeProviderType(providerType);
  }, [
    location.search,
    location.state,
    providerType,
  ]);

  useEffect(() => {
    const validateUser = async (): Promise<void> => {
      if (isUserValidated) {
        return;
      }

      try {
        console.log("[Home] Triggering ValidateOrCreateUser");

        await validateOrCreateUser();

        console.log("[Home] User validated from backend");

        setIsUserValidated(true);
      } catch (error: unknown) {
        logError(
          "[Home] Error during ValidateOrCreateUser",
          error
        );
      }
    };

    void validateUser();
  }, [isUserValidated, setIsUserValidated]);

  useEffect(() => {
    try {
      const resolvedProviderType =
        targetProviderType || Constants.RSKey;

      console.log(
        "[Home] Applying dashboard providerType",
        {
          resolvedProviderType,
          search: location.search,
        }
      );

      if (providerType !== resolvedProviderType) {
        setProviderType(resolvedProviderType);
      }

      setProviderTypeReady(true);
    } catch (error: unknown) {
      logError(
        "[Home] Error applying dashboard providerType",
        error
      );

      setProviderTypeReady(true);
    }
  }, [
    targetProviderType,
    providerType,
    setProviderType,
    location.search,
  ]);

  const handleDashboardProviderTypeChange = (
    value: string
  ) => {
    try {
      const normalizedValue =
        normalizeProviderType(value) || Constants.RSKey;

      setProviderType(normalizedValue);

      navigate(
        `/dashboard?providerType=${normalizedValue}`,
        {
          replace: false,
          state: {
            providerType: normalizedValue,
            reviewYear,
          },
        }
      );
    } catch (error: unknown) {
      logError(
        "[Home] Error changing dashboard providerType",
        error
      );
    }
  };

  const items = [
    {
      label: "Dashboard",
      path: "/dashboard",
    },
  ];

  const isSCO =
    providerType === Constants.SCOKey;

  const title = isSCO
    ? "PBC Scoring Portal"
    : "Reviewer Dashboard";

  if (!providerTypeReady) {
    return (
      <div
        style={{
          marginTop: "42px",
          marginBottom: "50px",
          marginLeft: "5px",
          padding: "16px",
        }}
      >
        Loading dashboard...
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: "42px",
        marginBottom: "50px",
        marginLeft: "5px",
      }}
    >
      <ComponentHeader
        title={title}
        breadcrumbs={items}
        showProviderTypeDropdown
        providerType={providerType}
        onProviderTypeChange={
          handleDashboardProviderTypeChange
        }
        reviewYear={reviewYear}
      />

      {isSCO ? (
        <SCOProvidersComponent
          providerType={providerType}
          reviewYear={reviewYear}
        />
      ) : (
        <RHProvidersComponent
          providerType={providerType}
          reviewYear={reviewYear}
        />
      )}
    </div>
  );
}

export default Home;